// Opens under the status buttons when Mark Returned is refused with reason_required:
// what is still out, and a required reason to close the order anyway.
import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { FieldRow, TextArea } from '../components/ui/Field';
import Panel from '../components/ui/Panel';

export interface StillOut {
  label: string;
  /** Went out. The API names it `shipped`. */
  shipped: number;
  returned?: number;
  ordered?: number;
}

interface Props {
  missing: StillOut[];
  /** Resolves when the attempt finished; the panel stays open on a failure so the reason is kept. */
  onClose: (reason: string) => Promise<void> | void;
  onCancel: () => void;
}

const CloseAsReturnedPanel: React.FC<Props> = ({ missing, onClose, onCancel }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const trimmed = reason.trim();

  // Opens by a click that refused; the cursor goes to the one thing to fill in.
  useEffect(() => {
    ref.current?.focus();
    ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const submit = async () => {
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onClose(trimmed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel on="panel" label="Close as returned" className="mt-4">
      <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-on-surface">Not everything is back yet.</p>
        {missing.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-sm text-on-surface-variant">
            {missing.map((m, i) => (
              <li key={`${m.label}-${i}`}>
                {m.label}: <span className="tabular-nums">{m.shipped} went out, {m.returned ?? 0} back</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <FieldRow label="Why close it anyway?">
        <TextArea
          ref={ref}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
            if (e.key === 'Escape') onCancel();
          }}
          rows={2}
          placeholder="One drone lost in transit"
        />
      </FieldRow>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={submit} disabled={!trimmed || busy} className={buttonClass('filled', 'md')}>
          Close as returned
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass('quiet', 'md')}>
          Cancel
        </button>
      </div>
      </div>
    </Panel>
  );
};

export default CloseAsReturnedPanel;
